import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  Typography,
  Chip,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Paper,
  Stack,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Checkbox,
  FormControlLabel,
  Avatar,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import {
  AccountBalanceWallet as WalletIcon,
  Send as SendIcon,
  Receipt as HistoryIcon,
  QrCode as QrCodeIcon,
  Security as SecurityIcon,
  ArrowBack as ArrowBackIcon,
  ContentCopy as CopyIcon,
  CheckCircle as CheckIcon,
  VolunteerActivism as DonationsIcon,
  CreditCard as CardIcon,
  AccountBalance as FiatIcon,
  Loyalty as RewardsIcon,
  Add as DepositIcon,
  Refresh as RefreshIcon,
  SwapHoriz as SwapIcon,
  VolunteerActivism as VolunteerActivismIcon,
} from "@mui/icons-material";
import { shortenAddress } from "../../../utils/addressUtils";
import { useAppSelector, useAppDispatch } from "../../../hooks/hooks";
import SendSection from "../../wallet/SendSection";
import ReceiveSection from "../../wallet/ReceiveSection";
import HistorySection from "../../wallet/HistorySection ";
import CardsSection from "../../wallet/CardsSection";
import WalletCreator from "../../wallet/WalletCreator";
import { setSelectedWalletTab } from "../../../redux/uiSlice";
import { setWalletAddress } from "../../../redux/userSlice";
import {
  initializeContract,
  refreshBalances,
  refreshPoints,
} from "../../../redux/depositsSlice";
import StackableBox from "../../StackableBox";
import { combineAndDecryptEVMWalletPrivateKey } from "../../../utils/security";
import { ethers } from "ethers";
import {
  fetchPolBalance,
  fetchUserBalance,
  initializeTokenContract,
  refreshTokenData,
} from "../../../redux/ndeipiCoinSlice";
import SmartContractService from "../../../services/smartContractService";
import { formatBalance } from "../../../utils/balanceUtils";
import DepositSection from "../../wallet/DepositSection";
import VirtualCard from "../../VirtualCard";
import DonationsSection from "../../wallet/DonationsSection";
import {
  getGiftTokenBalance,
  TokenItem,
  setSelectedToken,
} from "../../../redux/walletSlice";
import { useRewards } from "../../../hooks/useRewards";
import {
  AssetBalanceDto,
  AssetDto,
  getUsersAssetBalance,
} from "../../../redux/assetSlice";
import SwapSection from "../../wallet/SwapSection";
import { set } from "date-fns";

type BalanceType = "crypto" | "bank" | "rewards";
type WalletSection =
  | "main"
  | "send"
  | "deposit"
  | "receive"
  | "swap"
  | "history"
  | "security"
  | "donate"
  | "cards"
  | "create"
  | "tokens";

const MOBILE_APP_WIDTH = parseInt(
  process.env.REACT_APP_MOBILE_APP_WIDTH || "500"
);

const WalletView: React.FC = () => {
  const theme = useTheme();
  const isMobileView = useMediaQuery(`(max-width:${MOBILE_APP_WIDTH}px)`);
  const [copied, setCopied] = useState(false);
  const [balanceType, setBalanceType] = useState<BalanceType>("crypto");
  // const [selectedToken, setSelectedToken] = useState<TokenItem | null>(null);
  const { selectedToken } = useAppSelector((state) => state.wallet);
  const [walletView, setWalletView] = useState<"wallet" | "tokens" | "history">(
    "wallet"
  );

  const { selectedWalletTab } = useAppSelector((state) => state.ui);

  const { balance } = useAppSelector((state) => state.wallet);
  const { user } = useAppSelector((state) => state.user);
  const { chainType } = useAppSelector((state) => state.wallet);
  const { userPoints } = useAppSelector((state) => state.deposits);
  const { userBalance } = useAppSelector((state) => state.ndeipiCoin);
  const showSwap: boolean = false;
  const showAssets: boolean = false;
  const dispatch = useAppDispatch();
  const {
    balance: rewardsBalance,
    transactions,
    loading: rewardsLoading,
    error: rewardsError,
    fetchBalance: fetchRewardsBalance,
    fetchTransactionHistory: fetchRewardsTransactionHistory,
    convertPointsToUsdb,
    creditVolunteerTime,
  } = useRewards();

  // Mock balances for different types
  const balances = {
    crypto: {
      value: balance || "0",
      currency: chainType === "evm" ? "NDEIPI" : "MOVEUSD",
    },
    bank: { value: "1,245.67", currency: "USD" },
    rewards: { value: "5,250", currency: "Points" },
  };

  const handleCopyAddress = () => {
    if (!user) return;
    navigator.clipboard.writeText(user?.walletAddress);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const scrollableBoxStyles = {
    maxHeight: isMobileView ? "68vh" : "50vh",
    overflowY: "auto",
    scrollbarWidth: "none", // For Firefox
    "&::-webkit-scrollbar": {
      display: "none", // For Chrome, Safari, and Opera
    },
  };

  const handleDeposit = (amount: string) => {
    // Implement your deposit logic here
    console.log(`Depositing $${amount}`);
    // You might want to dispatch an action to your Redux store
    // dispatch(processDeposit(amount));

    // Show success message or handle the deposit
    // Then navigate back
    dispatch(setSelectedWalletTab("main"));
  };

  const handleBalanceTypeChange = (
    event: React.MouseEvent<HTMLElement>,
    newBalanceType: BalanceType | null
  ) => {
    if (newBalanceType !== null) {
      setBalanceType(newBalanceType);
    }
  };

  const handleWalletViewChange = (
    event: React.MouseEvent<HTMLElement>,
    newView: "wallet" | "tokens" | "history" | null
  ) => {
    if (newView !== null) {
      setWalletView(newView);
      dispatch(
        setSelectedWalletTab(
          newView === "wallet"
            ? "main"
            : newView === "tokens"
            ? "tokens"
            : "history"
        )
      );
    }
  };

  const renderBalance = () => {
    // When chainType is 'evm' and we have a selected token, show that balance

    if (chainType === "evm" && selectedToken) {
      return (
        <Typography
          variant="h6"
          gutterBottom
          sx={{ fontSize: isMobileView ? "1.5rem" : "2rem" }}
        >
          {selectedToken.balance !== null ? (
            `${formatBalance(selectedToken.balance)} ${selectedToken.symbol}`
          ) : (
            <CircularProgress size={24} />
          )}
        </Typography>
      );
    }

    // Original logic for non-evm chain types
    let currentBalance = balances[balanceType];

    // if (chainType === "evm") {
    balances.crypto = {
      value: userBalance || "0",
      currency: "NDEIPI",
    };
    currentBalance = balances.crypto;
    // } else {
    //   balances.crypto = {
    //     value: userBalance || "0",
    //     currency: "USDB",
    //   };
    //   currentBalance = balances.crypto;
    // }

    return (
      <Typography
        variant="h6"
        gutterBottom
        sx={{ fontSize: isMobileView ? "1.5rem" : "2rem" }}
      >
        {currentBalance.value !== null ? (
          `${formatBalance(currentBalance.value)} ${currentBalance.currency}`
        ) : (
          <CircularProgress size={24} />
        )}
      </Typography>
    );
  };

  const handleBack = async () => {
    await dispatch(refreshTokenData());
    dispatch(setSelectedWalletTab("main"));
  };

  const getSectionTitle = () => {
    switch (selectedWalletTab) {
      case "send":
        return chainType === "evm" ? "Send Assets" : "Send Tokens";
      case "receive":
        return "Receive Assets";
      case "swap":
        return "Swap Tokens";
      case "history":
        return "Transaction History";
      case "security":
        return "Security Settings";
      case "donate":
        return "Donations";
      case "cards":
        return "Cards";
      case "deposit":
        return "Add money";
      case "create":
        return "Create Wallet";
      case "tokens":
        return "Tokens";
      default:
        return "Wallet";
    }
  };

  const renderHeader = () => (
    <>
      <Box sx={{ p: isMobileView ? 1 : 2, textAlign: "start" }}>
        <Typography
          variant="h4"
          sx={{ fontSize: isMobileView ? "1.5rem" : "2rem" }}
        >
          Wallet
        </Typography>
        <Typography sx={{ fontSize: isMobileView ? "0.875rem" : "1rem" }}>
          Manage your wallet settings and preferences.
        </Typography>
      </Box>
      <Divider sx={{ mb: 2 }} />
    </>
  );

  const renderActionButtons = () => (
    <Stack direction="row" spacing={0} justifyContent="center" sx={{ mb: 3 }}>
      {/* <StackableBox
        label="Deposit"
        icon={<DepositIcon fontSize="medium" />}
        onClick={() => dispatch(setSelectedWalletTab("receive"))}
        isFirst
        isActive={selectedWalletTab === ("deposit" as WalletSection)}
      /> */}

      {/* {chainType !== "evm" && (
        <StackableBox
          label="Deposit"
          icon={<DepositIcon fontSize="medium" />}
          onClick={async () => {
            //await dispatch(fetchPolBalance());
            dispatch(setSelectedWalletTab("deposit"));
          }}
          isActive={selectedWalletTab === ("deposit" as WalletSection)}
        />
      )} */}

      <StackableBox
        label="Send"
        icon={<SendIcon fontSize="medium" />}
        onClick={async () => {
          //await dispatch(fetchPolBalance());
          dispatch(setSelectedWalletTab("send"));
        }}
        isActive={selectedWalletTab === ("send" as WalletSection)}
      />

      <StackableBox
        label="Receive"
        icon={<QrCodeIcon fontSize="medium" />}
        onClick={() => dispatch(setSelectedWalletTab("receive"))}
        isActive={selectedWalletTab === ("send" as WalletSection)}
      />
      {chainType !== "evm" && showSwap && (
        <StackableBox
          label="Swap"
          icon={<SwapIcon fontSize="medium" />}
          onClick={async () => {
            //await dispatch(fetchPolBalance());
            dispatch(setSelectedWalletTab("send"));
          }}
          isActive={selectedWalletTab === ("send" as WalletSection)}
        />
      )}

      {/* <StackableBox
        label="Swap"
        icon={<SwapIcon fontSize="medium" />}
        onClick={() => dispatch(setSelectedWalletTab("receive"))}
        isLast
        isActive={selectedWalletTab === ("swap" as WalletSection)}
      /> */}

      {/* <Button
        variant="contained"
        startIcon={<DepositIcon />}
        onClick={() => dispatch(setSelectedWalletTab("receive"))}
        sx={{
          flex: 1,
          py: 1.5,
          textTransform: "none",
          borderRadius: 2,
        }}
      >
        Deposit
      </Button> */}

      {/* <Button
        variant="contained"
        startIcon={<SendIcon />}
        onClick={() => dispatch(setSelectedWalletTab("send"))}
        sx={{
          flex: 1,
          py: 1.5,
          textTransform: "none",
          borderRadius: 2,
        }}
      >
        Send
      </Button> */}
      {/* <Button
        variant="contained"
        startIcon={<QrCodeIcon />}
        onClick={() => dispatch(setSelectedWalletTab("receive"))}
        sx={{
          flex: 1,
          py: 1.5,
          textTransform: "none",
          borderRadius: 2,
        }}
      >
        Request
      </Button> */}
      {chainType === "evm" &&
        selectedToken &&
        selectedToken.symbol !== "NDEIPI" && (
          <StackableBox
            label="Swap"
            icon={<SwapIcon fontSize="medium" />}
            onClick={async () => {
              //await dispatch(fetchPolBalance());
              dispatch(setSelectedWalletTab("swap"));
            }}
            isActive={selectedWalletTab === ("swap" as WalletSection)}
          />
        )}
    </Stack>
  );

  const renderSectionContent = () => {
    switch (selectedWalletTab) {
      case "send":
        return <SendSection />;
      case "receive":
        return <ReceiveSection />;
      case "swap":
        return <SwapSection isMobile={isMobileView} />;
      case "history":
        return <HistorySection />;
      case "security":
        return <SecuritySection />;
      case "donate":
        return <DonationsSection />;
      case "cards":
        return <CardsSection />;
      case "tokens":
        return <TokensSection isMobile={isMobileView} />;
      case "deposit":
        return (
          <DepositSection
            onBack={() => {
              dispatch(setSelectedWalletTab("main"));
            }}
            onDeposit={handleDeposit}
          />
        );
      case "create":
        return (
          <WalletCreator
            onCreateWallet={(address: string) => {
              dispatch(setWalletAddress(address));
              dispatch(setSelectedWalletTab("main"));
            }}
          />
        );
      default:
        return (
          <>
            <Box sx={{ p: isMobileView ? 1 : 3, textAlign: "center" }}>
              {/*<ToggleButtonGroup
                value={balanceType}
                exclusive
                onChange={handleBalanceTypeChange}
                aria-label="balance type"
                sx={{ mb: 2, flexWrap: isMobileView ? "wrap" : "nowrap" }}
                orientation={isMobileView ? "vertical" : "horizontal"}
              >
                <ToggleButton
                  value="crypto"
                  aria-label="crypto"
                  size={isMobileView ? "small" : "medium"}
                >
                  <WalletIcon fontSize="small" sx={{ mr: 1 }} />
                  {chainType === "evm" ? "Crypto" : "MOVEUSD"}
                </ToggleButton>
                <ToggleButton
                  value="bank"
                  aria-label="bank"
                  size={isMobileView ? "small" : "medium"}
                >
                  <FiatIcon fontSize="small" sx={{ mr: 1 }} />
                  Bank
                </ToggleButton>
                <ToggleButton
                  value="rewards"
                  aria-label="rewards"
                  size={isMobileView ? "small" : "medium"}
                >
                  <RewardsIcon fontSize="small" sx={{ mr: 1 }} />
                  Rewards
                </ToggleButton> 
              </ToggleButtonGroup>*/}

              <ToggleButtonGroup
                value={walletView}
                exclusive
                onChange={handleWalletViewChange}
                aria-label="balance type"
                sx={{ mb: 2, flexWrap: isMobileView ? "wrap" : "nowrap" }}
              >
                <ToggleButton
                  value="wallet"
                  aria-label="crypto"
                  size={isMobileView ? "small" : "medium"}
                >
                  Wallet
                </ToggleButton>

                <ToggleButton
                  value="tokens"
                  aria-label="crypto"
                  size={isMobileView ? "small" : "medium"}
                >
                  Tokens
                </ToggleButton>
                <ToggleButton
                  value="history"
                  aria-label="crypto"
                  size={isMobileView ? "small" : "medium"}
                >
                  History
                </ToggleButton>
              </ToggleButtonGroup>

              {renderBalance()}

              <Box sx={{ mt: 1, mb: 2, textAlign: "center" }}>
                <Typography
                  variant="body2"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  Primary Wallet
                </Typography>
              </Box>

              {user?.walletAddress && chainType === "evm" && (
                <Chip
                  label={
                    copied ? (
                      <>
                        <CheckIcon fontSize="small" sx={{ mr: 1 }} />
                        Copied!
                      </>
                    ) : (
                      <>
                        {user?.walletAddress}
                        <CopyIcon fontSize="small" sx={{ ml: 1 }} />
                      </>
                    )
                  }
                  onClick={handleCopyAddress}
                  sx={{
                    cursor: "pointer",
                    fontSize: isMobileView ? "0.75rem" : "0.875rem",
                  }}
                />
              )}
            </Box>
            <Divider sx={{ mb: 2 }} />

            {/* Action Buttons */}
            {renderActionButtons()}

            {/* Balance Type Toggle */}
            {/* <Box sx={{ textAlign: "center", mb: 2 }}>
              <ToggleButtonGroup
                value={balanceType}
                exclusive
                onChange={handleBalanceTypeChange}
                aria-label="balance type"
              >
                <ToggleButton value="crypto" aria-label="crypto">
                  <WalletIcon fontSize="small" sx={{ mr: 1 }} />
                  {chainType === "evm" ? "Crypto" : "MOVEUSD"}
                </ToggleButton>
                <ToggleButton value="bank" aria-label="bank">
                  <FiatIcon fontSize="small" sx={{ mr: 1 }} />
                  Bank
                </ToggleButton>
                <ToggleButton value="rewards" aria-label="rewards">
                  <RewardsIcon fontSize="small" sx={{ mr: 1 }} />
                  Rewards
                </ToggleButton>
              </ToggleButtonGroup>

            {/* Other menu items */}
            <Stack
              spacing={1}
              sx={{ ...scrollableBoxStyles, p: isMobileView ? 2 : 3 }}
            >
              {!user?.walletAddress ? (
                <Button
                  variant="contained"
                  startIcon={<WalletIcon />}
                  onClick={() => dispatch(setSelectedWalletTab("create"))}
                  fullWidth
                  sx={{ justifyContent: "center", py: 1.5 }}
                >
                  Create Wallet
                </Button>
              ) : (
                <>
                  {chainType === "solana" && (
                    <Box sx={{ mb: 3 }}>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{ fontWeight: "bold", mb: 1 }}
                        >
                          Virtual Cards{" "}
                        </Typography>

                        {/* <Button
                          variant="text"
                          size="small"
                          onClick={() =>
                            dispatch(setSelectedWalletTab("cards"))
                          }
                        >
                          {" "}
                          Link
                        </Button> */}
                      </Box>

                      {chainType === "solana" && (
                        <Box sx={{ mb: 2 }}>
                          <VirtualCard
                            cardHolderName={user.fullName}
                            key={user.name}
                          />
                        </Box>
                      )}

                      {(chainType as any) === "evm" && (
                        <List
                          sx={{ bgcolor: "background.paper", borderRadius: 1 }}
                        >
                          <ListItem>
                            <ListItemIcon>
                              <CardIcon />
                            </ListItemIcon>
                            <ListItemText
                              primary="MasterCard"
                              secondary={
                                <>
                                  <span>Card XXX</span>
                                </>
                              }
                              slotProps={{
                                secondary: {
                                  component: "div",
                                  sx: {
                                    display: "flex",
                                    justifyContent: "space-between",
                                  },
                                },
                              }}
                            />
                          </ListItem>
                        </List>
                      )}
                    </Box>
                  )}

                  {chainType === "solana" && showAssets && (
                    <Box sx={{ mb: 4 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Assets
                      </Typography>
                      <List
                        sx={{ bgcolor: "background.paper", borderRadius: 1 }}
                      >
                        <ListItem>
                          <ListItemIcon>
                            <RewardsIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary="NDEIPI"
                            secondary={
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                <span>NDEIPI Balance</span>
                                <span>$0.00</span>
                              </Box>
                            }
                            slotProps={{
                              secondary: {
                                component: "div",
                              },
                            }}
                          />
                        </ListItem>
                      </List>
                    </Box>
                  )}

                  {chainType === "solana" && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Rewards & Balances
                      </Typography>
                      <List
                        sx={{ bgcolor: "background.paper", borderRadius: 1 }}
                      >
                        {/* Transfer Balance */}
                        <ListItem>
                          <ListItemIcon>
                            <SwapIcon color="primary" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Transfer Balance"
                            secondary={
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <span>
                                  {rewardsLoading ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    `${
                                      rewardsBalance?.transferBalance || 0
                                    } Points`
                                  )}
                                </span>
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={() => {
                                    // Add conversion to USDB functionality
                                    if (
                                      rewardsBalance?.transferBalance &&
                                      rewardsBalance.transferBalance > 0
                                    ) {
                                      convertPointsToUsdb(
                                        rewardsBalance.transferBalance
                                      )
                                        .then(() => {
                                          // Refresh balance after conversion
                                          fetchRewardsBalance();
                                        })
                                        .catch((error: any) => {
                                          console.error(
                                            "Conversion failed:",
                                            error
                                          );
                                        });
                                    }
                                  }}
                                  disabled={
                                    !rewardsBalance?.transferBalance ||
                                    rewardsBalance.transferBalance <= 0
                                  }
                                  sx={{ minWidth: "80px" }}
                                >
                                  Convert
                                </Button>
                              </Box>
                            }
                            slotProps={{
                              secondary: {
                                component: "div",
                              },
                            }}
                          />
                        </ListItem>

                        {/* Donation Balance */}
                        <ListItem>
                          <ListItemIcon>
                            <DonationsIcon color="secondary" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Donation Balance"
                            secondary={
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <span>
                                  {rewardsLoading ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    `${
                                      rewardsBalance?.donationBalance || 0
                                    } Points`
                                  )}
                                </span>
                                <Chip
                                  label="Service Only"
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                />
                              </Box>
                            }
                            slotProps={{
                              secondary: {
                                component: "div",
                              },
                            }}
                          />
                        </ListItem>

                        {/* Volunteer Balance */}
                        <ListItem>
                          <ListItemIcon>
                            <VolunteerActivismIcon color="success" />
                          </ListItemIcon>
                          <ListItemText
                            primary="Volunteer Balance"
                            secondary={
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <span>
                                  {rewardsLoading ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    `${
                                      rewardsBalance?.volunteerBalance || 0
                                    } Points`
                                  )}
                                </span>
                                <Chip
                                  label="Service Only"
                                  size="small"
                                  variant="outlined"
                                  color="success"
                                />
                              </Box>
                            }
                            slotProps={{
                              secondary: {
                                component: "div",
                              },
                            }}
                          />
                        </ListItem>

                        {/* Social Impact Number */}
                        {rewardsBalance?.socialImpactNumber && (
                          <ListItem>
                            <ListItemIcon>
                              <SecurityIcon color="info" />
                            </ListItemIcon>
                            <ListItemText
                              primary="Social Impact Number"
                              secondary={
                                <Box
                                  sx={{
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                  }}
                                >
                                  <span>
                                    {rewardsBalance.socialImpactNumber}
                                  </span>
                                  <Chip
                                    label="Verified"
                                    size="small"
                                    variant="filled"
                                    color="info"
                                  />
                                </Box>
                              }
                              slotProps={{
                                secondary: {
                                  component: "div",
                                },
                              }}
                            />
                          </ListItem>
                        )}

                        {/* Refresh Button */}
                        <ListItem>
                          <Button
                            startIcon={<RefreshIcon />}
                            onClick={() => {
                              fetchRewardsBalance();
                              fetchRewardsTransactionHistory();
                            }}
                            disabled={rewardsLoading}
                            fullWidth
                            variant="outlined"
                            sx={{ mt: 1 }}
                          >
                            {rewardsLoading
                              ? "Refreshing..."
                              : "Refresh Balances"}
                          </Button>
                        </ListItem>
                      </List>

                      {/* Error Display */}
                      {rewardsError && (
                        <Box
                          sx={{
                            mt: 1,
                            p: 1,
                            bgcolor: "error.light",
                            borderRadius: 1,
                          }}
                        >
                          <Typography variant="body2" color="error.dark">
                            Error loading rewards: {rewardsError}
                          </Typography>
                        </Box>
                      )}

                      {/* Quick Actions */}
                      <Box
                        sx={{
                          mt: 2,
                          display: "flex",
                          gap: 1,
                          flexDirection: "column",
                        }}
                      >
                        <Button
                          variant="contained"
                          startIcon={<VolunteerActivismIcon />}
                          onClick={() => {
                            // Navigate to volunteer hours credit page or open modal
                            // This would integrate with your volunteer hours system
                            console.log("Credit volunteer hours");
                          }}
                          fullWidth
                          size="small"
                        >
                          Credit Volunteer Hours
                        </Button>

                        <Button
                          variant="outlined"
                          startIcon={<HistoryIcon />}
                          onClick={() => {
                            // Navigate to rewards transaction history
                            console.log("View rewards history");
                          }}
                          fullWidth
                          size="small"
                        >
                          View Rewards History
                        </Button>
                      </Box>
                    </Box>
                  )}

                  {/*

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="h6" sx={{ fontWeight: "bold", mb: 1 }}>
                      Activity
                    </Typography>
                    <List sx={{ bgcolor: "background.paper", borderRadius: 1 }}>
                      <ListItem
                        onClick={() => {
                          dispatch(setSelectedWalletTab("history"));
                        }}
                      >
                        <ListItemIcon>
                          <RewardsIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary="Transaction History"
                          secondary={
                            <Box
                              sx={{
                                display: "flex",
                                justifyContent: "space-between",
                              }}
                            >
                              
                            </Box>
                          }
                          slotProps={{
                            secondary: {
                              component: "div",
                            },
                          }}
                        />
                      </ListItem>
                    </List>
                  </Box> */}

                  {/* <Box sx={{ mb: 3 }}>
                    {chainType === "evm" ? (
                      <TokensSection
                        isMobile={isMobileView}
                        //onTokenSelect={setSelectedToken}
                      />
                    ) : null}
                  </Box> */}

                  {/* <Button
                    variant="outlined"
                    startIcon={<CardIcon />}
                    onClick={() => dispatch(setSelectedWalletTab("cards"))}
                    fullWidth
                    sx={{ justifyContent: "flex-start", py: 1.5 }}
                  >
                    Cards
                  </Button> */}
                  {/* <Button
                    variant="outlined"
                    startIcon={<HistoryIcon />}
                    onClick={async () => {
                      if (user?.walletAddress) {
                        await dispatch(
                          fetchTransactions({
                            accountAddress: user.walletAddress,
                          })
                        );
                        dispatch(setSelectedWalletTab("history"));
                      }
                    }}
                    fullWidth
                    sx={{ justifyContent: "flex-start", py: 1.5 }}
                  >
                    Transaction History
                  </Button> */}
                  {/* <Button
                    variant="outlined"
                    startIcon={<SecurityIcon />}
                    onClick={() => dispatch(setSelectedWalletTab("security"))}
                    fullWidth
                    sx={{ justifyContent: "flex-start", py: 1.5 }}
                  >
                    Security & Privacy
                  </Button> */}
                  {"solana" === chainType && (
                    <Box sx={{ mb: 3 }}>
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: "bold", mb: 1 }}
                      >
                        Donations
                      </Typography>
                      <List
                        sx={{ bgcolor: "background.paper", borderRadius: 1 }}
                      >
                        <ListItem
                          onClick={() =>
                            dispatch(setSelectedWalletTab("donate"))
                          }
                        >
                          <ListItemIcon>
                            <DonationsIcon />
                          </ListItemIcon>
                          <ListItemText
                            primary="Donations History"
                            secondary={
                              <Box
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                }}
                              >
                                {/* <span>0.95 Points</span>
                              <span>$0.95</span> */}
                              </Box>
                            }
                            slotProps={{
                              secondary: {
                                component: "div",
                              },
                            }}
                          />
                        </ListItem>
                      </List>
                    </Box>
                  )}
                </>
              )}
            </Stack>
          </>
        );
    }
  };

  // useEffect(() => {
  //   //  Ndeipi token initialized when component mounts
  //   if (chainType === "evm") {
  //     const initialToken: TokenItem = {
  //       id: "ndeipi",
  //       name: "Ndeipi Token",
  //       symbol: "NDEIPI",
  //       balance: userBalance || "0.000", // Use actual balance
  //       isChecked: false,
  //       isSelected: true,
  //     };
  //     dispatch(setSelectedToken(initialToken));
  //   }
  // }, [dispatch, chainType, userBalance]);

  useEffect(() => {
    fetchRewardsBalance();
    fetchRewardsTransactionHistory();
  }, [fetchRewardsBalance, fetchRewardsTransactionHistory]);

  useEffect(() => {
    const initWalletUi = async () => {
      try {
        if (chainType === "evm") {
          //the user is using the window wallet and has not set up their security token
          const provider = new ethers.JsonRpcProvider(
            process.env.REACT_APP_POLYGON_RPC_URL || "https://polygon-rpc.com/"
          );

          // console.log("Connecting to wallet...");

          // console.log("Initializing EVM contract...");

          if (user?.walletAddress && !user?.securityTokenA) {
            //this is for the who us using metamask.
            await Promise.all([
              dispatch(initializeTokenContract()),
              dispatch(initializeContract()),
            ]);
          }

          if (user?.walletAddress && user?.securityTokenA) {
            await dispatch(fetchUserBalance());
            // const pk = combineAndDecryptEVMWalletPrivateKey([
            //   user?.securityTokenA ?? "",
            //   user?.verificationCodeB ?? "",
            //   user?.accessPhraseC ?? "",
            //   user?.identityMarkerD ?? "",
            // ]);

            // SmartContractService.initializeContractsInternal(
            //   pk,
            //   user?.walletAddress
            // ).then(async () => {
            //   await Promise.all([
            //     dispatch(initializeTokenContract()),
            //     dispatch(refreshTokenData()),
            //     //dispatch(initializeContract()),
            //   ]);
            // });
          }

          // const depositsContract = SmartContractService.getDepositsContract();
          // const ndeipiTokenContract =
          //   SmartContractService.getNdeipiTokenContract();

          // if (!depositsContract || !ndeipiTokenContract) {
          //   SmartContractService.initializeContracts();
          // }

          // await Promise.all([
          //   dispatch(
          //     initializeContract({
          //       provider,
          //       privateKey: pk,
          //       chainType: chainType,
          //     })
          //   ).unwrap(),
          //   dispatch(
          //     initializeTokenContract({ provider, privateKey: pk })
          //   ).unwrap(),
          // ]);
        } else {
          // console.log("Initializing Solana program...");
          //await dispatch(initializeSolanaProgram({type: 'browser'})).unwrap();
        }
      } catch (error) {
        console.error("Error initializing wallet:", error);
        // Handle error, e.g., show a notification or alert
      }
    };
    if (selectedWalletTab === "main") {
      initWalletUi();
      setWalletView("wallet");
    }
  }, [selectedWalletTab, user, chainType, dispatch]);

  // useEffect(() => {
  //   const fetchData = async () => {
  //     //Refresh balances and points
  //     dispatch(refreshBalances());
  //     // console.log("Fetching transactions for user:", user?.walletAddress);
  //     if (user?.walletAddress) {
  //       const pk = combineAndDecryptEVMWalletPrivateKey([
  //         user?.securityTokenA ?? "",
  //         user?.verificationCodeB ?? "",
  //         user?.accessPhraseC ?? "",
  //         user?.identityMarkerD ?? "",
  //       ]);
  //       await dispatch(
  //         refreshPoints({ userAddress: user.walletAddress, privateKey: pk })
  //       );
  //     }
  //   };
  //   fetchData();
  // }, [user?.walletAddress, dispatch, chainType]);

  return (
    <Paper
      sx={{
        width: "100%",
        maxWidth: MOBILE_APP_WIDTH,
        margin: "auto",
        height: "100%",
        overflow: "auto",
        // Mobile styles
        ...(isMobileView && {
          maxWidth: "100%",
          borderRadius: 0,
          boxShadow: "none",
        }),
        // Desktop styles
        ...(!isMobileView && {
          borderRadius: 2,
          boxShadow: 3,
        }),
      }}
    >
      <>
        {renderHeader()}
        {selectedWalletTab !== "main" && (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              p: isMobileView ? 1 : 2,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <IconButton onClick={handleBack} sx={{ mr: 1 }}>
              <ArrowBackIcon fontSize={isMobileView ? "small" : "medium"} />
            </IconButton>
            <Typography
              variant="h6"
              sx={{ fontSize: isMobileView ? "1.25rem" : "1.5rem" }}
            >
              {getSectionTitle()}
            </Typography>
          </Box>
        )}
      </>

      <Box
        sx={{
          width: "100%",
          height: "calc(100% - 64px)",
          overflow: "auto",
          WebkitOverflowScrolling: "touch", // Smooth scrolling on iOS
        }}
      >
        {renderSectionContent()}
      </Box>
    </Paper>
  );
};

// const SwapSection = ({ isMobile }: { isMobile: boolean }) => {
//   return (
//     <Box sx={{ p: isMobile ? 1 : 2 }}>
//       <Typography
//         variant="h6"
//         gutterBottom
//         sx={{ fontSize: isMobile ? "1.25rem" : "1.5rem" }}
//       >
//         Swap Tokens
//       </Typography>
//       <Typography sx={{ fontSize: isMobile ? "0.875rem" : "1rem" }}>
//         This section would contain a token swap interface.
//       </Typography>
//     </Box>
//   );
// };

const SecuritySection = () => {
  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6" gutterBottom>
        Security Settings
      </Typography>
      <Typography>
        This section would contain wallet security options.
      </Typography>
    </Box>
  );
};

// const DonationsSection = () => {
//   return (
//     <Box sx={{ p: 2 }}>
//       <Typography variant="h6" gutterBottom>
//         Charitable Donations
//       </Typography>
//       <Typography>
//         This section would contain Charitable Donations options.
//       </Typography>
//     </Box>
//   );
// };

// New Tokens Section Component
interface TokensSectionProps {
  isMobile: boolean;
  //onTokenSelect: (token: TokenItem | null) => void;
}

const TokensSection: React.FC<TokensSectionProps> = ({
  isMobile,
  //onTokenSelect,
}) => {
  const [hideZeroBalance, setHideZeroBalance] = useState(false);
  const { giftTokenBalance } = useAppSelector((state) => state.wallet);
  const { userBalance } = useAppSelector((state) => state.ndeipiCoin);

  // const [selectedToken, setSelectedToken] = useState<TokenItem | null>(null);
  const { selectedToken } = useAppSelector((state) => state.wallet);
  const { assetBalances } = useAppSelector((state) => state.assets);
  const [isLoading, setIsLoading] = useState(true);
  const [tokens, setTokens] = useState<TokenItem[]>([
    {
      id: "ndeipi",
      name: "Ndeipi Token",
      symbol: "NDEIPI",
      balance: "0.000",
      isChecked: false,
      isSelected: true, // Set Ndeipi as default selected
      address:
        process.env.REACT_APP_NDEIPICOIN_CONTRACT_ADDRESS ||
        "0xD8d401df77080Baa60718FA0b1f775eea272ab3b",
    },
    {
      id: "gift",
      name: "GIFT Token",
      symbol: "GIFT",
      balance: "0.000",
      isChecked: false,
      address:
        process.env.REACT_APP_GIFT_TOKEN_CONTRACT_ADDRESS ||
        "0xCfde7c43EDB3c9f71331AAc1003b099CE40c94ea",
    },
  ]);

  const handleHideZeroBalanceChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setHideZeroBalance(event.target.checked);
  };

  const handleTokenCheckboxChange = (tokenId: string) => {
    setTokens(
      tokens.map((token) =>
        token.id === tokenId ? { ...token, isChecked: !token.isChecked } : token
      )
    );
  };

  const handleTokenClick = (tokenId: string) => {
    // Update local state first
    setTokens((prevTokens) => {
      const updatedTokens = prevTokens.map((token) => ({
        ...token,
        isSelected: token.id === tokenId,
      }));

      // Find the clicked token from the updated array
      const clickedToken = updatedTokens.find((token) => token.id === tokenId);

      if (clickedToken) {
        // Dispatch with the updated token data
        dispatch(setSelectedToken(clickedToken));
        dispatch(setSelectedWalletTab("main"));
      }

      return updatedTokens;
    });
  };

  const filteredTokens = hideZeroBalance
    ? tokens.filter((token) => parseFloat(token.balance) > 0)
    : tokens;

  const dispatch = useAppDispatch();

  useEffect(() => {
    // Fetch token data from an API or blockchain here
    dispatch(getGiftTokenBalance());
  }, [dispatch]);

  useEffect(() => {
    // Fetch all assets
    dispatch(getUsersAssetBalance());
  }, [dispatch]);

  // useEffect(() => {
  //   const ndeipiToken = tokens.find((token) => token.id === "ndeipi");
  //   if (ndeipiToken) {
  //     dispatch(setSelectedToken(ndeipiToken));
  //   }
  // }, []);

  useEffect(() => {
    // Update token balances based on fetched data
    // console.log("Gift Token Balance:", giftTokenBalance);
    // console.log("User Ndeipi Balance:", userBalance);
    setTokens((prevTokens) =>
      prevTokens.map((token) => {
        if (token.id === "gift") {
          return { ...token, balance: giftTokenBalance || "0.000" };
        } else if (token.id === "ndeipi") {
          return { ...token, balance: userBalance || "0.000" };
        } else {
          return token;
        }
      })
    );
  }, [giftTokenBalance, userBalance]);

  useEffect(() => {
    if (assetBalances && assetBalances.length > 0) {
      setTokens((prevTokens) => {
        // Keep GIFT and Ndeipi tokens
        const specialTokens = prevTokens.filter(
          (token) => token.id === "gift" || token.id === "ndeipi"
        );

        // Transform assets to TokenItem format, excluding Ndeipi if it exists in assets
        const assetTokens: TokenItem[] = assetBalances
          .filter(
            (asset: AssetBalanceDto) => asset.asset.symbol !== "NDEIPI" // Don't duplicate Ndeipi
          )
          .map((asset: AssetBalanceDto) => ({
            id: asset.id,
            name: asset.asset.name,
            symbol: asset.asset.symbol,
            balance: asset.balance.toString(),
            isChecked: false,
            isSelected: false,
            assetId: asset.asset.id,
            address: asset.asset.contractAddress || "",
          }));

        // Update Ndeipi balance if found in assetBalances
        const ndeipiAsset = assetBalances.find(
          (asset: AssetBalanceDto) => asset.asset.symbol === "NDEIPI"
        );

        if (ndeipiAsset) {
          const updatedSpecialTokens = specialTokens.map((token) =>
            token.id === "ndeipi"
              ? { ...token, balance: ndeipiAsset.balance.toString() }
              : token
          );

          return [...updatedSpecialTokens, ...assetTokens];
        }

        return [...specialTokens, ...assetTokens];
      });

      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  }, [assetBalances]);

  return (
    <Box
      sx={{
        mt: 4,
        p: isMobile ? 2 : 3,
        height: "100%",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* Section Header */}

      {/* Hide Zero Balance Toggle */}
      <FormControlLabel
        control={
          <Checkbox
            checked={hideZeroBalance}
            onChange={handleHideZeroBalanceChange}
            size={isMobile ? "small" : "medium"}
          />
        }
        label={
          <Typography
            variant="body2"
            sx={{ fontSize: isMobile ? "0.8rem" : "0.875rem" }}
          >
            Hide zero balance tokens
          </Typography>
        }
        sx={{ mb: 2 }}
      />

      {/* Tokens List */}
      {isLoading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Box
          sx={{
            flex: 1,
            maxHeight: isMobile ? "60vh" : "70vh", // Responsive height
            overflowY: "auto",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": {
              display: "none",
            },
            WebkitOverflowScrolling: "touch",
          }}
        >
          {filteredTokens.map((token) => (
            <TokenListItem
              key={token.id}
              token={token}
              isMobile={isMobile}
              onCheckboxChange={handleTokenCheckboxChange}
              onTokenClick={handleTokenClick}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

interface TokenListItemProps {
  token: TokenItem;
  isMobile: boolean;
  onCheckboxChange: (tokenId: string) => void;
  onTokenClick: (tokenId: string) => void;
}

const TokenListItem: React.FC<TokenListItemProps> = ({
  token,
  isMobile,
  onCheckboxChange,
  onTokenClick,
}) => {
  const theme = useTheme();
  const hasBalance = parseFloat(token.balance) > 0;

  return (
    <Paper
      elevation={1}
      sx={{
        p: isMobile ? 1.5 : 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        borderRadius: 2,
        border: `1px solid ${
          token.isSelected ? theme.palette.primary.main : theme.palette.divider
        }`,
        borderWidth: token.isSelected ? 2 : 1,
        transition: "all 0.2s ease",
        cursor: "pointer", // Add cursor pointer
        "&:hover": {
          boxShadow: 2,
          borderColor: theme.palette.primary.light,
        },
      }}
      onClick={() => onTokenClick(token.id)}
    >
      {/* Left side: Checkbox and Token Info */}
      <Box
        sx={{ display: "flex", alignItems: "center", gap: isMobile ? 1.5 : 2 }}
        onClick={(e) => e.stopPropagation()}
      >
        <Checkbox
          checked={token.isChecked}
          onChange={() => onCheckboxChange(token.id)}
          size={isMobile ? "small" : "medium"}
          sx={{
            color: theme.palette.primary.main,
            "&.Mui-checked": {
              color: theme.palette.primary.main,
            },
          }}
        />

        {/* Token Avatar/Icon */}
        <Box sx={{ position: "relative" }}>
          <Avatar
            sx={{
              width: isMobile ? 32 : 40,
              height: isMobile ? 32 : 40,
              bgcolor: token.isSelected
                ? theme.palette.primary.main
                : theme.palette.grey[400],
              fontSize: isMobile ? "0.8rem" : "1rem",
              fontWeight: 600,
            }}
            onClick={() => onTokenClick(token.id)}
          >
            {token.symbol.charAt(0)}
          </Avatar>
          {token.isSelected && (
            <Box
              sx={{
                position: "absolute",
                top: -4,
                right: -4,
                width: 12,
                height: 12,
                borderRadius: "50%",
                bgcolor: theme.palette.primary.main,
                border: `2px solid ${theme.palette.background.paper}`,
              }}
            />
          )}
        </Box>

        {/* Token Name and Symbol */}
        <Box onClick={() => onTokenClick(token.id)}>
          <Typography
            variant="body1"
            sx={{
              fontSize: isMobile ? "0.9rem" : "1rem",
              fontWeight: 600,
              color: token.isSelected
                ? theme.palette.primary.main
                : "text.primary",
            }}
          >
            {token.name}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontSize: isMobile ? "0.75rem" : "0.875rem",
              color: "text.secondary",
            }}
          >
            {token.symbol}
          </Typography>
        </Box>
      </Box>

      {/* Right side: Balance */}
      <Box sx={{ textAlign: "right" }}>
        <Typography
          variant="body1"
          sx={{
            fontSize: isMobile ? "0.9rem" : "1rem",
            fontWeight: 600,
            color: token.isSelected
              ? theme.palette.primary.main
              : hasBalance
              ? "text.primary"
              : "text.secondary",
          }}
        >
          {token.balance}
        </Typography>
        {!hasBalance && (
          <Chip
            label={hasBalance ? "Balance" : "Zero Balance"}
            size="small"
            variant="outlined"
            sx={{
              fontSize: isMobile ? "0.6rem" : "0.7rem",
              height: isMobile ? 20 : 24,
              bgcolor: hasBalance ? theme.palette.success.light : "transparent",
              borderColor: hasBalance
                ? theme.palette.success.main
                : theme.palette.divider,
              color: hasBalance ? theme.palette.success.dark : "text.secondary",
            }}
          />
        )}
        {hasBalance && (
          <Chip
            label={"Swap"}
            size="small"
            variant="filled"
            sx={{
              fontSize: isMobile ? "0.6rem" : "0.7rem",
              height: isMobile ? 20 : 24,
              bgcolor: theme.palette.primary.main,
              fontWeight: "bold",
              //borderColor: theme.palette.success.main,
              //color: theme.palette.success.dark,
            }}
            onClick={(e) => {
              // Handle swap action
              e.stopPropagation();
              console.log(`Swapping ${token.symbol}`);
            }}
          />
        )}
      </Box>
    </Paper>
  );
};

export default WalletView;
